import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useState } from "react";
import { QData } from "../../../types/basic-type-list";
import { IDeleteDataResponseAndPayload, TResponse, TResponseWithQData } from "../../../types/common";
import {
  IRecurringSchedule,
  IRecurringScheduleCreatePayload,
  IRecurringScheduleUpdatePayload,
  IRecurringScheduleRunNowResult,
} from "./types";
import {
  RECURRING_SCHEDULE_ENDPOINTS,
  listRecurringSchedules,
  recurringScheduleDetail,
  createRecurringSchedule,
  updateRecurringSchedule,
  deleteRecurringSchedule,
  toggleRecurringScheduleActive,
  runRecurringScheduleNow,
} from "./callbacks";

export const useRecurringSchedulesList = (
  qData: QData,
  options?: Omit<UseQueryOptions<TResponseWithQData<IRecurringSchedule[]>, Error>, "queryKey" | "queryFn">
): UseQueryResult<TResponseWithQData<IRecurringSchedule[]>, Error> & {
  refetchWithQuery: (params: Partial<QData>) => void;
} => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });

  const queryResult = useQuery<TResponseWithQData<IRecurringSchedule[]>, Error>({
    queryKey: [RECURRING_SCHEDULE_ENDPOINTS.LIST, { ...queryParams, total: null }],
    queryFn: async () => {
      const res = await listRecurringSchedules(queryParams);
      return res.data;
    },
    select: (res) => ({
      ...res,
      data: (res.data ?? []).map((x) => ({ key: x.id, ...x })),
      qData: { ...queryParams, total: res.extra?.total },
    }),
    initialData: {
      data: [],
      success: true,
      message: "",
      code: 200,
      qData: { page: 1, limit: 20 },
      extra: { page: 1, limit: 20, total: 0 },
    },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
    refetchWithQuery: (params: Partial<QData>) => {
      setQueryParams(params as QData);
    },
  };
};

export const useRecurringScheduleDetail = (
  id: string | number | null | undefined,
  options?: Omit<UseQueryOptions<TResponse<IRecurringSchedule>, Error>, "queryKey" | "queryFn">
): UseQueryResult<TResponse<IRecurringSchedule>, Error> => {
  return useQuery<TResponse<IRecurringSchedule>, Error>({
    queryKey: [RECURRING_SCHEDULE_ENDPOINTS.DETAIL, id],
    queryFn: async () => {
      const res = await recurringScheduleDetail(id!);
      return res.data;
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 60000,
    ...options,
  });
};

export const useCreateRecurringSchedule = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IRecurringSchedule>) => void;
    onError?: (error: unknown) => void;
  } = {},
  options: UseMutationOptions<TResponse<IRecurringSchedule>, unknown, IRecurringScheduleCreatePayload> = {}
): UseMutationResult<TResponse<IRecurringSchedule>, unknown, IRecurringScheduleCreatePayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IRecurringSchedule>, unknown, IRecurringScheduleCreatePayload>({
    mutationFn: async (data: IRecurringScheduleCreatePayload) => {
      const res = await createRecurringSchedule(data);
      return res.data;
    },
    onSuccess: (res, ...rest) => {
      queryClient.invalidateQueries({ queryKey: [RECURRING_SCHEDULE_ENDPOINTS.LIST] });
      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

export const useUpdateRecurringSchedule = (
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: (res: TResponse<IRecurringSchedule>) => void;
    onError?: (error: unknown) => void;
  } = {},
  options: UseMutationOptions<TResponse<IRecurringSchedule>, unknown, IRecurringScheduleUpdatePayload> = {}
): UseMutationResult<TResponse<IRecurringSchedule>, unknown, IRecurringScheduleUpdatePayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IRecurringSchedule>, unknown, IRecurringScheduleUpdatePayload>({
    mutationFn: async (data: IRecurringScheduleUpdatePayload) => {
      const res = await updateRecurringSchedule(data);
      return res.data;
    },
    onSuccess: (res, ...rest) => {
      queryClient.invalidateQueries({ queryKey: [RECURRING_SCHEDULE_ENDPOINTS.LIST] });
      queryClient.setQueryData([RECURRING_SCHEDULE_ENDPOINTS.DETAIL, res.data?.id], res);
      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

export const useDeleteRecurringSchedule = (
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: (res: TResponse<IDeleteDataResponseAndPayload>) => void;
    onError?: (error: unknown) => void;
  } = {},
  options: UseMutationOptions<
    TResponse<IDeleteDataResponseAndPayload>,
    unknown,
    IDeleteDataResponseAndPayload
  > = {}
): UseMutationResult<TResponse<IDeleteDataResponseAndPayload>, unknown, IDeleteDataResponseAndPayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IDeleteDataResponseAndPayload>, unknown, IDeleteDataResponseAndPayload>({
    mutationFn: async (data: IDeleteDataResponseAndPayload) => {
      const res = await deleteRecurringSchedule(data);
      return res.data;
    },
    onSuccess: (res, ...rest) => {
      queryClient.invalidateQueries({ queryKey: [RECURRING_SCHEDULE_ENDPOINTS.LIST] });
      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

export const useToggleRecurringScheduleActive = (
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: (res: TResponse<IRecurringSchedule>) => void;
    onError?: (error: unknown) => void;
  } = {},
  options: UseMutationOptions<TResponse<IRecurringSchedule>, unknown, string | number> = {}
): UseMutationResult<TResponse<IRecurringSchedule>, unknown, string | number> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IRecurringSchedule>, unknown, string | number>({
    mutationFn: async (id: string | number) => {
      const res = await toggleRecurringScheduleActive(id);
      return res.data;
    },
    onSuccess: (res, ...rest) => {
      queryClient.invalidateQueries({ queryKey: [RECURRING_SCHEDULE_ENDPOINTS.LIST] });
      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

export const useRunRecurringScheduleNow = (
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: (res: TResponse<IRecurringScheduleRunNowResult>) => void;
    onError?: (error: unknown) => void;
  } = {},
  options: UseMutationOptions<TResponse<IRecurringScheduleRunNowResult>, unknown, string | number> = {}
): UseMutationResult<TResponse<IRecurringScheduleRunNowResult>, unknown, string | number> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IRecurringScheduleRunNowResult>, unknown, string | number>({
    mutationFn: async (id: string | number) => {
      const res = await runRecurringScheduleNow(id);
      return res.data;
    },
    onSuccess: (res, ...rest) => {
      queryClient.invalidateQueries({ queryKey: [RECURRING_SCHEDULE_ENDPOINTS.LIST] });
      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};
